import React, { useMemo, useState, useEffect } from 'react';
import { Box, Chip, TextField } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import {
  chartColors,
  gridProps,
  axisTick,
  quietAxisProps,
  tooltipContentStyle,
  barRadius,
  MAX_BAR_SIZE,
  SERIES_FILL_OPACITY,
  activeBarProps,
  legendProps,
} from './chartTheme';

type Granularity = 'DAY' | 'WEEK' | 'MONTH';

interface Row {
  date: string;
  income: number;
  expenses: number;
}

interface Props {
  data: Row[];
  loading?: boolean;
}

function bucketKey(dateIso: string, g: Granularity): string {
  const d = new Date(dateIso);
  if (g === 'DAY') return dateIso;
  if (g === 'MONTH') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return monday.toISOString().slice(0, 10);
}

function bucketLabel(key: string, g: Granularity): string {
  if (g === 'MONTH') {
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, {
      month: 'short',
      year: '2-digit',
    });
  }
  if (g === 'WEEK') {
    return `Wk ${new Date(key).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }
  return new Date(key).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const IncomeExpenseBarChart: React.FC<Props> = ({ data, loading }) => {
  const { theme } = useTheme();
  const [granularity, setGranularity] = useState<Granularity>('DAY');

  const { dataMin, dataMax } = useMemo(() => {
    if (!data || data.length === 0) return { dataMin: '', dataMax: '' };
    const sorted = [...data].map((r) => r.date).sort();
    return { dataMin: sorted[0], dataMax: sorted[sorted.length - 1] };
  }, [data]);

  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');

  // Sync internal range when incoming data range changes
  useEffect(() => {
    setFrom(dataMin);
    setTo(dataMax);
  }, [dataMin, dataMax]);

  const filtered = useMemo(() => {
    if (!data || data.length === 0) return [];
    const lo = from || dataMin;
    const hi = to || dataMax;
    return data.filter((r) => r.date >= lo && r.date <= hi);
  }, [data, from, to, dataMin, dataMax]);

  const bucketed = useMemo(() => {
    if (filtered.length === 0) return [];
    const map = new Map<string, { key: string; income: number; expenses: number }>();
    filtered.forEach((row) => {
      const key = bucketKey(row.date, granularity);
      const prev = map.get(key);
      if (prev) {
        prev.income += row.income || 0;
        prev.expenses += row.expenses || 0;
      } else {
        map.set(key, { key, income: row.income || 0, expenses: row.expenses || 0 });
      }
    });
    return Array.from(map.values())
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((b) => ({
        ...b,
        label: bucketLabel(b.key, granularity),
      }));
  }, [filtered, granularity]);

  if (loading) {
    return (
      <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gray?.[400] }}>
        Loading…
      </Box>
    );
  }

  const incomeColor = chartColors.income;
  const expenseColor = chartColors.expense;

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '8px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#DDD8D2' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.primary },
    },
    '& .MuiInputBase-input': { color: theme.text, fontSize: '0.78rem', py: 0.75 },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666', fontSize: '0.75rem' },
  };

  return (
    <Box>
      {/* Controls row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {(['DAY', 'WEEK', 'MONTH'] as Granularity[]).map((g) => {
            const active = granularity === g;
            return (
              <Chip
                key={g}
                label={g === 'DAY' ? 'Daily' : g === 'WEEK' ? 'Weekly' : 'Monthly'}
                onClick={() => setGranularity(g)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 28,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: active ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                  color: active ? theme.primary : theme.gray?.[500] || '#666',
                  border: active ? `1.5px solid ${theme.primary}` : '1px solid transparent',
                  '&:hover': { backgroundColor: active ? `${theme.primary}30` : theme.gray?.[200] || '#E0E0E0' },
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: { sm: 'auto' } }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dataMin, max: dataMax }}
            sx={{ ...inputSx, minWidth: 138 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dataMin, max: dataMax }}
            sx={{ ...inputSx, minWidth: 138 }}
          />
        </Box>
      </Box>

      {bucketed.length === 0 ? (
        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gray?.[400] }}>
          No data in selected range
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={bucketed} margin={{ top: 8, right: 16, left: 8, bottom: 0 }} barCategoryGap={1} barGap={0}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="label" tick={axisTick} {...quietAxisProps} />
            <YAxis
              tick={axisTick}
              tickFormatter={(v: number) => formatCurrency(v, { showDecimals: false, compact: true })}
              {...quietAxisProps}
            />
            <Tooltip
              formatter={(value: number, name: string) => [formatCurrency(value, { showDecimals: false }), name]}
              contentStyle={tooltipContentStyle}
              cursor={{ fill: `${theme.primary}11` }}
            />
            <Legend {...legendProps} />
            <ReferenceLine y={0} stroke={gridProps.stroke} />
            <Bar
              dataKey="income"
              name="Income"
              fill={incomeColor}
              fillOpacity={SERIES_FILL_OPACITY}
              radius={barRadius}
              maxBarSize={MAX_BAR_SIZE}
              {...activeBarProps}
            />
            <Bar
              dataKey="expenses"
              name="Expenses"
              fill={expenseColor}
              fillOpacity={SERIES_FILL_OPACITY}
              radius={barRadius}
              maxBarSize={MAX_BAR_SIZE}
              {...activeBarProps}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default IncomeExpenseBarChart;
